import { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  BackofficePageModeActionMenu,
  CustomizableDetailTabs,
  PageHeader,
  panelSpanFromLayout,
  useCustomizableDetailTabs,
  useBackofficePageMode,
  TagsTile,
} from '@/components/shared';
import { getTagColorClassName } from '@/components/shared/tag-colors';
import { useBackofficePageTitle } from '@/lib/page-title';
import { ProjectsListSection } from '../projects/components';
import { fetchBackofficeUserDetail } from './api';
import {
  UserDetailPanelGrid,
  UserDetailPanel,
  UserCommunicationPreferencesTile,
  UserCommunicationsSection,
  UserDetailsPanel,
  UserLastEmailSentTile,
  UserOrganizationsPanel,
  UserSingleValuePanel,
} from './components';
import { formatRelativeLastSeen, formatUserDisplayName } from './format';
import { buildPosthogUserActivityUrl } from './posthogActivityUrl';
import type { BackofficeUserDetail } from './types';

const POSTHOG_ACTIVITY_TOOLTIP = 'Last 7 days activity in Posthog';
const INTERNAL_TESTER_TAG_ID = 'internal-tester';
type UserTab = 'overview' | 'studies' | 'communication';

const USER_BUILT_IN_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'studies', label: 'Studies' },
  { id: 'communication', label: 'Communication' },
] satisfies Array<{ id: UserTab; label: string }>;

export function BackofficeUserDetailsPage() {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<BackofficeUserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const displayName = user ? formatUserDisplayName(user) : null;
  const internalTesterTag = user?.tags?.find((tag) => tag.id === INTERNAL_TESTER_TAG_ID) ?? null;
  const lastSeenOnline = user ? formatRelativeLastSeen(user.lastSeenOnlineAt) : null;
  const { pageMode, setPageMode } = useBackofficePageMode();
  const detailBasePath = userId ? `/users/${userId}` : '/users';
  const detailTabs = useCustomizableDetailTabs<UserTab>({
    preferenceScope: 'details:user:tabs',
    builtInTabs: USER_BUILT_IN_TABS,
    defaultTabId: 'overview',
    basePath: detailBasePath,
    pageMode,
  });
  const activeTab = detailTabs.activeBuiltInTabId;
  const activeCommunicationTileId =
    searchParams.get('communicationTab') === 'in-app'
      ? 'in-app-communications-table'
      : 'email-communications-table';

  useBackofficePageTitle(displayName ? `User: ${displayName}` : 'User');

  function getTabHref(tab: UserTab, updates: Record<string, string | null> = {}) {
    return detailTabs.getTabHref(tab, updates);
  }

  useEffect(() => {
    if (!userId) {
      setUser(null);
      setError('User not found');
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchBackofficeUserDetail(userId, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) {
          setUser(response.user);
        }
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setUser(null);
        setError(caught instanceof Error ? caught.message : 'Failed to load user');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [userId]);

  const createUserSummaryTileItems = (loadedUser: BackofficeUserDetail, startY: number) => [
    {
      id: 'user-details',
      title: 'User details',
      description: 'Core profile, contact, locale, and account metadata for this user.',
      layout: {
        x: 0,
        y: startY,
        w: 2,
        h: 3,
        minW: 1,
        minH: 1,
        maxW: 4,
        maxH: 4,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <UserDetailsPanel
          user={loadedUser}
          panelId="user-details"
          currentSpan={panelSpanFromLayout(layout, '2x3')}
        />
      ),
    },
    {
      id: 'last-seen-online',
      title: 'Last seen online',
      description: 'Recent activity signal with a shortcut to the user activity view in Posthog.',
      layout: { x: 2, y: startY, w: 1, h: 1, isResizable: false },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <UserSingleValuePanel
          panelId="last-seen-online"
          currentSpan={panelSpanFromLayout(layout, '1x1')}
          title="Last seen online"
          value={lastSeenOnline?.value ?? 'Never'}
          footer={lastSeenOnline?.suffix ?? undefined}
          externalHref={buildPosthogUserActivityUrl(loadedUser.id)}
          linkLabel={`View last 7 days activity in Posthog for ${displayName ?? loadedUser.email}`}
          linkTitle={POSTHOG_ACTIVITY_TOOLTIP}
          linkTooltip={
            <span className="inline-flex items-center gap-1">
              {POSTHOG_ACTIVITY_TOOLTIP}
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </span>
          }
        />
      ),
    },
    {
      id: 'owner-of',
      title: 'Owner of',
      description: 'Count of studies owned by this user with a link to the studies tab.',
      layout: { x: 3, y: startY, w: 1, h: 1, isResizable: false },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <UserSingleValuePanel
          panelId="owner-of"
          currentSpan={panelSpanFromLayout(layout, '1x1')}
          title="Owner of"
          value={String(loadedUser.numberProjects)}
          footer={loadedUser.numberProjects === 1 ? 'study' : 'studies'}
          valueTone="neutral"
          linkTo={getTabHref('studies', { includeAllWithAccess: null })}
          linkLabel={`View studies owned by ${displayName ?? loadedUser.email}`}
        />
      ),
    },
    {
      id: 'shared-study-access',
      title: 'Access to',
      description: 'Count of shared studies this user can access with a filtered studies link.',
      layout: { x: 2, y: startY + 1, w: 1, h: 1, isResizable: false },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <UserSingleValuePanel
          panelId="shared-study-access"
          currentSpan={panelSpanFromLayout(layout, '1x1')}
          title="Access to"
          value={String(loadedUser.sharedStudyAccessCount)}
          footer={loadedUser.sharedStudyAccessCount === 1 ? 'shared study' : 'shared studies'}
          valueTone="neutral"
          linkTo={getTabHref('studies', { includeAllWithAccess: 'true' })}
          linkLabel={`View studies ${displayName ?? loadedUser.email} has access to`}
        />
      ),
    },
    {
      id: 'organizations',
      title: 'Organizations',
      description: 'Organizations this user belongs to, including their role in each organization.',
      layout: {
        x: 2,
        y: startY + 2,
        w: 2,
        h: 2,
        minW: 1,
        minH: 1,
        maxW: 4,
        maxH: 4,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <UserOrganizationsPanel
          organizations={loadedUser.organizations}
          panelId="organizations"
          currentSpan={panelSpanFromLayout(layout, '2x2')}
        />
      ),
    },
    {
      id: 'tags',
      title: 'Tags',
      description: 'Tags assigned to this user.',
      layout: {
        x: 0,
        y: startY + 4,
        w: 2,
        h: 1,
        minW: 1,
        minH: 1,
        maxW: 4,
        maxH: 3,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <TagsTile
          entityType="user"
          entityId={loadedUser.id}
          panelId="tags"
          currentSpan={panelSpanFromLayout(layout, '2x1')}
        />
      ),
    },
  ];

  const createUserTableTileItems = (loadedUser: BackofficeUserDetail, startY: number) => [
    {
      id: 'studies-table',
      title: 'Studies table',
      description: 'Filter, sort, and open studies owned by or shared with this user.',
      layout: {
        x: 0,
        y: startY,
        w: 4,
        h: 4,
        minW: 2,
        minH: 2,
        maxW: 6,
        maxH: 5,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <UserDetailPanel
          title="Studies"
          span="4x4"
          panelId="studies-table"
          currentSpan={panelSpanFromLayout(layout, '4x4')}
          contentClassName="-mx-5 -mb-5"
          contentOverflow="hidden"
        >
          <ProjectsListSection
            fixedOwnerId={loadedUser.id}
            showOwnerFilter={false}
            showOwnerColumn={false}
            showAccessFilter
            preferenceScope={`list:user:${loadedUser.id}:studies`}
            surface="tile"
          />
        </UserDetailPanel>
      ),
    },
    {
      id: 'communication-preferences',
      title: 'Communication preferences',
      description: 'Global and study-specific email preference settings.',
      layout: {
        x: 0,
        y: startY + 4,
        w: 3,
        h: 2,
        minW: 2,
        minH: 2,
        maxW: 6,
        maxH: 4,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <UserDetailPanel
          title="Communication preferences"
          span="3x2"
          panelId="communication-preferences"
          currentSpan={panelSpanFromLayout(layout, '3x2')}
        >
          <UserCommunicationPreferencesTile userId={loadedUser.id} />
        </UserDetailPanel>
      ),
    },
    {
      id: 'last-email-sent',
      title: 'Last email sent',
      description: 'Most recent email communication sent to this user.',
      layout: { x: 3, y: startY + 4, w: 1, h: 1, isResizable: false },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <UserLastEmailSentTile
          userId={loadedUser.id}
          panelId="last-email-sent"
          currentSpan={panelSpanFromLayout(layout, '1x1')}
        />
      ),
    },
    {
      id: 'email-communications-table',
      title: 'Email communications',
      description: 'Filter, sort, and inspect email communications sent to this user.',
      layout: {
        x: 0,
        y: startY + 6,
        w: 4,
        h: 4,
        minW: 2,
        minH: 2,
        maxW: 6,
        maxH: 5,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <UserDetailPanel
          title="Email communications"
          span="4x4"
          panelId="email-communications-table"
          currentSpan={panelSpanFromLayout(layout, '4x4')}
          contentClassName="-mx-5 -mb-5"
          contentOverflow="hidden"
        >
          <UserCommunicationsSection
            userId={loadedUser.id}
            userDisplayName={formatUserDisplayName(loadedUser) ?? loadedUser.email}
            channel="email"
            showSummary={false}
            showSubtabs={false}
            surface="tile"
          />
        </UserDetailPanel>
      ),
    },
    {
      id: 'in-app-communications-table',
      title: 'In-app notifications',
      description: 'Filter, sort, and inspect in-app notifications sent to this user.',
      layout: {
        x: 0,
        y: startY + 10,
        w: 4,
        h: 4,
        minW: 2,
        minH: 2,
        maxW: 6,
        maxH: 5,
        isResizable: true,
      },
      render: (layout: Parameters<typeof panelSpanFromLayout>[0]) => (
        <UserDetailPanel
          title="In-app notifications"
          span="4x4"
          panelId="in-app-communications-table"
          currentSpan={panelSpanFromLayout(layout, '4x4')}
          contentClassName="-mx-5 -mb-5"
          contentOverflow="hidden"
        >
          <UserCommunicationsSection
            userId={loadedUser.id}
            userDisplayName={formatUserDisplayName(loadedUser) ?? loadedUser.email}
            channel="in-app"
            showSummary={false}
            showSubtabs={false}
            surface="tile"
          />
        </UserDetailPanel>
      ),
    },
  ];

  return (
    <main className="min-h-[320px] bg-neutral-50 p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        <PageHeader
          actions={
            <BackofficePageModeActionMenu
              pageMode={pageMode}
              onPageModeChange={setPageMode}
              label="User page actions"
            />
          }
          showShareButton={pageMode !== 'edit'}
          adornment={
            internalTesterTag ? (
              <span
                className={[
                  'inline-flex rounded-full border px-2.5 py-1 text-small font-medium',
                  getTagColorClassName(internalTesterTag.color),
                ].join(' ')}
              >
                {internalTesterTag.name}
              </span>
            ) : null
          }
        >
          {displayName ?? 'User'}
        </PageHeader>

        <CustomizableDetailTabs tabs={detailTabs} />

        {loading ? (
          <div className="rounded-lg border border-neutral-200 bg-white px-5 py-16 text-center text-body text-neutral-500 shadow-sm">
            Loading user
          </div>
        ) : error ? (
          <div className="rounded-lg border border-neutral-200 bg-white px-5 py-16 text-center text-body text-red-700 shadow-sm">
            {error}
          </div>
        ) : user && activeTab === 'overview' ? (
          <UserDetailPanelGrid
            resetKey={user.id}
            preferenceScope="details:user:overview"
            layoutMode={pageMode}
            defaultVisibleItemIds={[
              'user-details',
              'last-seen-online',
              'owner-of',
              'shared-study-access',
              'organizations',
            ]}
            tileCatalogLabel="Add user tile"
            items={[...createUserSummaryTileItems(user, 0), ...createUserTableTileItems(user, 4)]}
          />
        ) : user && detailTabs.activeCustomTab ? (
          <UserDetailPanelGrid
            resetKey={detailTabs.activeCustomTab.id}
            preferenceScope={`details:user:${detailTabs.activeCustomTab.id}`}
            layoutMode={pageMode}
            defaultVisibleItemIds={[]}
            tileCatalogLabel="Add user tile"
            items={[...createUserSummaryTileItems(user, 0), ...createUserTableTileItems(user, 4)]}
          />
        ) : user && activeTab === 'studies' ? (
          <UserDetailPanelGrid
            resetKey={user.id}
            preferenceScope="details:user:studies"
            layoutMode={pageMode}
            defaultVisibleItemIds={['studies-table']}
            tileCatalogLabel="Add user tile"
            items={[...createUserTableTileItems(user, 0), ...createUserSummaryTileItems(user, 8)]}
          />
        ) : user && activeTab === 'communication' ? (
          <UserDetailPanelGrid
            resetKey={user.id}
            preferenceScope="details:user:communication:v2"
            layoutMode={pageMode}
            defaultVisibleItemIds={[
              'communication-preferences',
              'last-email-sent',
              activeCommunicationTileId,
            ]}
            tileCatalogLabel="Add user tile"
            items={[...createUserTableTileItems(user, 0), ...createUserSummaryTileItems(user, 8)]}
          />
        ) : null}
      </div>
    </main>
  );
}
